import { addDays, endOfMonth, format, startOfMonth } from 'date-fns';

import { action } from 'lib/actionHelpers.js';
import { createSelector } from 'reselect';
import { formatGraphData } from 'screens/reports/lib/paidAdsHelpers';
import { sortByProps } from '../lib/sortByProp.js';

const stableArray = [];
const stableObject = {};
let currentDataMap = {};

const accountTotals = {
  Sales: ['Spend', 'Revenue'],
  Reach: ['Spend', 'Reach'],
  Leads: ['Spend', 'Revenue'],
  Video: [],
};

const buildAdwordsDataTypes = (type) => {
  const baseDataTypes = {
    Impressions: { friendly: 'Impressions', api: 'Impressions', selector: 'impressionsDailyStatsSelector', order: 3 },
    Cost_conv: { friendly: 'Cost Conv', api: 'CostPerConversion', selector: 'costPerConversionDailyStatsSelector', order: 5, isCurrency: true },
    CostPerConversion: {
      friendly: 'Cost Conv',
      api: 'CostPerConversion',
      selector: 'costPerConversionDailyStatsSelector',
      order: 5,
    },
    Conversions: { friendly: 'Conversions', api: 'Conversions', selector: 'conversionsDailyStatsSelector', order: 5 },
    CTR: { friendly: 'CTR', api: 'Ctr', selector: 'ctrDailyStatsSelector', isPercentage: true },
    Conv_rate: { friendly: 'Conv Rate', api: undefined, selector: 'costPerConversionDailyStatsSelector', order: 5 },
  };
  if (type === 'Leads' || type === 'Sales') {
    baseDataTypes.roas = { friendly: 'ROAS', api: null };
    baseDataTypes.Clicks = { friendly: 'Clicks', api: 'Clicks', selector: 'clicksDailyStatsSelector', order: 5 };
    baseDataTypes.Cost = { friendly: 'Spend', api: null, selector: null, order: 1, isCurrency: true };
    delete baseDataTypes.Conv_rate;
  }
  if (type === 'Reach') {
    baseDataTypes.Cost = { friendly: 'Spend', api: null, selector: null, order: 1, isCurrency: true };
    baseDataTypes.Clicks = { friendly: 'Clicks', api: 'Clicks', selector: 'clicksDailyStatsSelector', order: 5 };
    baseDataTypes.ad_value = { friendly: 'Reach', api: null, selector: null, order: 2 };
    delete baseDataTypes.Conv_rate;
  }
  if (type === 'Video') {
    baseDataTypes.Views = { friendly: 'Video Views', api: 'VideoViews' };
    baseDataTypes.Clicks = { friendly: 'Clicks', api: 'Clicks', selector: 'clicksDailyStatsSelector', order: 5 };
    delete baseDataTypes.Conv_rate;
  }
  currentDataMap = baseDataTypes;
  return baseDataTypes;
};

const crmAdwordsSelectedCampaignClickableAction = ({ name }) => {
  return () => {
    return action('setGraphTypeAdwordsCampaigns', name);
  };
};

export const crmSiteDataPaidAds = (state) => state.sites.show.paidAdsData;
export const paidAdsDateCompareDateValue = (state) => state.ui.filters.paidAdsDateCompareDateValue;
export const crmPaidAdsDataCampaigns = (state) => state.crmAdwords.adwordsCampaigns;
export const crmAdwordsAdgroupsData = (state) => state.crmAdwords.adwordsAdGroups;
export const adwordsAdGroupsComparisonData = (state) => state.crmAdwords.adwordsAdGroupsComparisonData;
export const adwordsSelectedDataTypeForAdwordsGraph = (state) => state.crmAdwords.selectedGraphType;
export const adwordsCampaignsComparisonData = (state) => state.crmAdwords.adwordsCampaignsComparisonData;

export const crmPaidAdsStartDate = (state) => state.ui.boundForms.crmReportsScreen.state.dates.startDate;
export const crmPaidAdsEndDate = (state) => state.ui.boundForms.crmReportsScreen.state.dates.startDate;
export const crmTriggerFetchBool = (state) => state.ui.boundForms.crmReportsScreen.state.fetch;

export const selectedAdwordsCampaignIdState = (state) => state.crmAdwords.selectedAdwordsCampaignId;
export const crmSiteDataPaidAdsAdWordsID = (state) => state.sites.show.data.adwords_id;
export const accountLevelAdwordsData = (state) => state.sites.show.paidAdsData.adwordsPerformance;

export const adwordsSpendSelector = createSelector(crmAdwordsAdgroupsData, (crmAdwordsAdgroupsData) => {
  if ((crmAdwordsAdgroupsData || []).length) {
    const dataSet = crmAdwordsAdgroupsData[0];
    if (dataSet.Total_conv_value > 0) {
      return [
        { name: 'Spend', value: `$${dataSet.cost}` },
        { name: 'Revenue', value: dataSet.Total_conv_value },
      ];
    }
  }
  return [];
});

export const crmPaidAdsDataMCCAccounts = createSelector(crmSiteDataPaidAds, (crmSiteDataPaidAds) => (crmSiteDataPaidAds || stableObject).MCC_Accounts.map((item) => ({ ...item, label: item.name, value: item.id })));

export const paidAdsDateCompareDateValueSelector = createSelector(paidAdsDateCompareDateValue, (paidAdsDateCompareDateValue) => {
  if (!paidAdsDateCompareDateValue) {
    return undefined;
  }

  return {
    startDate: format(startOfMonth(new Date(paidAdsDateCompareDateValue)), 'yyyy-MM-dd'),
    endDate: format(endOfMonth(new Date(paidAdsDateCompareDateValue)), 'yyyy-MM-dd'),
  };
});

export const paidAdsDateCompareDateValueFormattedSelector = createSelector(paidAdsDateCompareDateValue, (paidAdsDateCompareDateValue) => {
  if (!paidAdsDateCompareDateValue) {
    return {};
  }

  return {
    paidAdsDateCompareMonthYear: format(startOfMonth(addDays(new Date(paidAdsDateCompareDateValue), 8)), 'MMMM yyyy'),
  };
});

export const crmPaidAdsDataCampaignsSelector = createSelector(crmPaidAdsDataCampaigns, (crmPaidAdsDataCampaigns) => {
  const val = (crmPaidAdsDataCampaigns || stableArray).map((item) => ({
    ...item,
    label: item.Campaign,
    value: item.Campaign_ID,
  }));
  return val;
});

export const selectedAdwordsCampaignId = createSelector(selectedAdwordsCampaignIdState, (selectedAdwordsCampaignIdState) => {
  return selectedAdwordsCampaignIdState;
});

export const crmSiteDataPaidAdsAdWordsIDSelector = createSelector(crmSiteDataPaidAdsAdWordsID, (crmSiteDataPaidAdsAdWordsID) => {
  return crmSiteDataPaidAdsAdWordsID;
});

export const crmStartAndEndDates = createSelector(crmPaidAdsStartDate, crmPaidAdsEndDate, crmTriggerFetchBool, (crmPaidAdsStartDate, crmPaidAdsEndDate, crmTriggerFetchBool) => {
  const lastDayOfStartDate = format(endOfMonth(addDays(new Date(crmPaidAdsStartDate), 3)), 'yyyy-MM-dd');
  return {
    startDate: crmPaidAdsStartDate,
    endDate: lastDayOfStartDate,
    triggerFetch: crmTriggerFetchBool,
    dates: { startDate: crmPaidAdsStartDate, endDate: lastDayOfStartDate },
  };
});
const campaignDataMapFormatter = (valueMap, item) => {
  return {
    name: valueMap[item[0]].friendly,
    api: valueMap[item[0]].api,
    originalName: item[0],
    value: isNaN(parseFloat(item[1])) ? item[1] : parseFloat(item[1]).toLocaleString(),
    isPlural: true,
    order: valueMap[item[0]].order,
    clickAction: valueMap[item[0]].api !== null ? crmAdwordsSelectedCampaignClickableAction({ name: item[0] }) : undefined,
    isCurrency: valueMap[item[0]].isCurrency,
  };
};

export const crmAdwordsSelectedCampaignData = createSelector(crmPaidAdsDataCampaigns, selectedAdwordsCampaignId, adwordsCampaignsComparisonData, accountLevelAdwordsData, (crmPaidAdsDataCampaigns, selectedAdwordsCampaignId, adwordsCampaignsComparisonData, accountLevelAdwordsData) => {
  let accountLevelTotals = {};
  if (accountLevelAdwordsData && (accountLevelAdwordsData || {}.totals)) {
    const { total } = accountLevelAdwordsData.totals;
    accountLevelTotals = total;
  }
  let final = [];
  const campaign = (crmPaidAdsDataCampaigns || []).find((item) => item.Campaign_ID === selectedAdwordsCampaignId);
  if (campaign) {
    const valueMap = buildAdwordsDataTypes(campaign.ad_label);
    final = Object.entries(campaign)
      .filter((item) => valueMap[item[0]])
      .map((item) => campaignDataMapFormatter(valueMap, item))
      .map((item) => {
        if (accountLevelTotals && accountLevelTotals[item.originalName] && accountTotals[campaign.ad_label].includes(item.name)) {
          const formattedValue = typeof item.value === 'string' ? parseInt((item.value || '').replace('$', '').replace(',', '').trim()) : parseInt(item.value);
          item.accountTotalPercentage = ((formattedValue / accountLevelTotals[item.originalName]) * 100).toFixed(1);
        }
        return item;
      });
  }

  const comparisonCampaign = (adwordsCampaignsComparisonData || []).find((item) => item.Campaign_ID === selectedAdwordsCampaignId);
  if (comparisonCampaign) {
    const valueMap = buildAdwordsDataTypes(comparisonCampaign.ad_label);
    const comparisonData = Object.entries(comparisonCampaign)
      .filter((item) => valueMap[item[0]])
      .map((item) => campaignDataMapFormatter(valueMap, item));
    const mappedComparisonValues = comparisonData.reduce((all, item) => {
      all[item.name] = parseFloat(item.value);
      return all;
    }, {});
    const includeCalculatedChange = final.map((item) => ({
      ...item,
      change: (parseFloat(item.value) - mappedComparisonValues[item.name]).toFixed(1),
    }));
    return includeCalculatedChange.sort(sortByProps(['order'], ['asc']));
  }
  return final.sort(sortByProps(['order'], ['asc']));
});

// Daily stats campaign data

export const costPerConversionDailyStats = (state) => state.crmAdwords.costPerConversionDailyStats;
export const conversionsDailyStats = (state) => state.crmAdwords.conversionsDailyStats;
export const clicksDailyStats = (state) => state.crmAdwords.clicksDailyStats;
export const impressionsDailyStats = (state) => state.crmAdwords.impressionsDailyStats;
export const ctrDailyStats = (state) => state.crmAdwords.ctrDailyStats;

// Comparison data daily stats
export const costPerConversionDailySecondDateStats = (state) => state.crmAdwords.costPerConversionDailySecondDateStats;
export const conversionsDailySecondDateStats = (state) => state.crmAdwords.conversionsDailySecondDateStats;
export const clicksDailySecondDateStats = (state) => state.crmAdwords.clicksDailySecondDateStats;
export const impressionsDailySecondDateStats = (state) => state.crmAdwords.impressionsDailySecondDateStats;
export const ctrDailySecondDateStats = (state) => state.crmAdwords.ctrDailySecondDateStats;

export const adwordsDataMapSelector = createSelector(adwordsSelectedDataTypeForAdwordsGraph, crmPaidAdsDataCampaigns, selectedAdwordsCampaignId, adwordsCampaignsComparisonData, accountLevelAdwordsData, (adwordsSelectedDataTypeForAdwordsGraph, crmPaidAdsDataCampaigns, selectedAdwordsCampaignId, adwordsCampaignsComparisonData, accountLevelAdwordsData) => {
  return currentDataMap[adwordsSelectedDataTypeForAdwordsGraph] || {};
});

export const costPerConversionDailyStatsSelector = createSelector(costPerConversionDailyStats, costPerConversionDailySecondDateStats, (costPerConversionDailyStats, costPerConversionDailySecondDateStats) => {
  return formatGraphData(costPerConversionDailyStats, 'Cost Conv', 'Cost_conv', costPerConversionDailySecondDateStats);
});

export const conversionsDailyStatsSelector = createSelector(conversionsDailyStats, conversionsDailySecondDateStats, (conversionsDailyStats, conversionsDailySecondDateStats) => {
  return formatGraphData(conversionsDailyStats, 'Conversions', 'Conversions', conversionsDailySecondDateStats, 'Conversions');
});

export const clicksDailyStatsSelector = createSelector(clicksDailyStats, clicksDailySecondDateStats, (clicksDailyStats, clicksDailySecondDateStats) => {
  return formatGraphData(clicksDailyStats, 'Clicks', 'Clicks', clicksDailySecondDateStats);
});

export const impressionsDailyStatsSelector = createSelector(impressionsDailyStats, impressionsDailySecondDateStats, (impressionsDailyStats, impressionsDailySecondDateStats) => {
  return formatGraphData(impressionsDailyStats, 'Impressions', 'Impressions', impressionsDailySecondDateStats);
});

export const ctrDailyStatsSelector = createSelector(ctrDailyStats, ctrDailySecondDateStats, (ctrDailyStats, ctrDailySecondDateStats) => {
  const formatFn = (item) => {
    if (typeof item.CTR === 'string' && item.CTR.includes('%')) {
      item.CTR = parseFloat(item.CTR.replace('%', '')).toFixed(2);
    }
    return item;
  };
  const formattedCTRDaily = (ctrDailyStats || []).map(formatFn);
  const formattedSecondDateStats = (ctrDailySecondDateStats || []).map(formatFn);
  return formatGraphData(formattedCTRDaily, 'CTR', 'CTR', formattedSecondDateStats, 'CTR');
});
